import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
    saveWechatJumpInfo,
    setRedirectingFlag,
    getAuthRedirectPath,
    clearAuthRedirectPath,
} from '../utils/wechatJumpManager';
import { isWechatBrowser } from '../config/wechat';

// 微信配置 - 使用动态路径
const WECHAT_CONFIG = {
    appId: process.env.REACT_APP_WECHAT_APP_ID || 'wx98ab678185dc8c67',
    // 动态获取回调地址，不使用写死的域名
    get redirectUri() {
        return encodeURIComponent(`${window.location.origin}/wechat-auth`);
    },
    scope: 'snsapi_userinfo',
    state: 'wechat_auth'
};

// 构建微信授权URL
const buildWechatAuthUrl = () => {
    // console.log('🔧 buildWechatAuthUrl: 开始构建微信授权URL');

    const appId = process.env.REACT_APP_WECHAT_APP_ID;
    // console.log('📱 微信AppID:', appId);

    const currentDomain = process.env.REACT_APP_AUTH_API_BASE_URL || window.location.origin;
    const redirectUri = encodeURIComponent(`${currentDomain}/wechat-auth`);
    // console.log('🔗 原始回调地址:', `${currentDomain}/wechat-auth`);
    // console.log('🔗 编码后回调地址:', redirectUri);

    const scope = 'snsapi_userinfo';

    // 动态设置state参数
    let state = 'wechat_auth'; // 默认值

    // 检查是否来自黑卡页面 / 表单详情 / 签到页面
    const fromBlackCard = localStorage.getItem('fromBlackCard');
    if (fromBlackCard === '1') {
        state = 'blackcard';
        localStorage.removeItem('fromBlackCard')
    } else {
        // 检查是否有保存的返回URL，如果有则使用其路径作为state
        const returnUrl = localStorage.getItem('returnUrl');
        if (returnUrl) {
            try {
                const url = new URL(returnUrl);
                const path = url.pathname;
                // 如果是表单详情页面，使用路径作为state
                if (path.startsWith('/detail/')) {
                    state = path.substring(1); // 移除开头的斜杠
                } else if (path.startsWith('/sign-in-sheet-form')) {
                    // 签到页面：只保存brand参数的值到state
                    // 例如：https://form.fangxiaojia.vip/sign-in-sheet-form?brand=%E6%B5%8B%E8%AF%953 -> sign_in_sheet:%E6%B5%8B%E8%AF%953
                    const brandValue = url.searchParams.get('brand') || '';
                    state = `sign_in_sheet:${brandValue}`;
                }
            } catch (error) {
                // console.warn('解析returnUrl失败，使用默认state:', error);
            }
        }
    }

    // console.log('📋 授权范围:', scope);
    // console.log('📋 状态参数:', state);

    localStorage.setItem('fromWechatForm', '1');
    // console.log('💾 已设置 fromWechatForm = 1');

    // 添加formId参数
    const formId = localStorage.getItem('wechatFormId');
    const formIdParam = formId ? `&formId=${formId}` : '';
    // console.log('📝 FormID:', formId);
    // console.log('📝 FormID参数:', formIdParam);
    const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}${formIdParam}#wechat_redirect`;
    // console.log('🔗 最终授权URL:', authUrl);
    // console.log('🔧 buildWechatAuthUrl: 构建完成');

    return authUrl;
};

export default function WechatAuth() {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [debugInfo, setDebugInfo] = useState('');

    useEffect(() => {
        // 检查是否正在处理微信授权，如果是则跳过
        // console.log('🚀 WechatAuth: 组件开始初始化');
        // console.log('📍 当前页面URL:', window.location.href);
        // console.log('📍 当前域名:', window.location.origin);
        // console.log('📍 User Agent:', navigator.userAgent);

        // 添加超时保护，避免长时间白屏
        const timeoutId = setTimeout(() => {
            if (loading) {
                // console.log('⚠️ WechatAuth: 超时保护触发，停止加载状态');
                setLoading(false);
                setError('页面加载超时，请刷新重试');
                // setDebugInfo('超时保护触发 - 10秒内未完成授权流程');
            }
        }, 15000); // 15秒超时

        // 收集调试信息 - 已注释
        // const debugData = {
        //     url: window.location.href,
        //     origin: window.location.origin,
        //     userAgent: navigator.userAgent,
        //     searchParams: Object.fromEntries(searchParams.entries()),
        //     localStorage: {
        //         wechatUser: localStorage.getItem('wechatUser'),
        //         returnUrl: localStorage.getItem('returnUrl'),
        //         isProcessingWechatAuth: localStorage.getItem('isProcessingWechatAuth'),
        //         fromBlackCard: localStorage.getItem('fromBlackCard'),
        //         wechatFormId: localStorage.getItem('wechatFormId')
        //     },
        //     timestamp: new Date().toISOString()
        // };
        // setDebugInfo(JSON.stringify(debugData, null, 2));

        // 检查是否有code参数，如果有则处理授权回调
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const formId = searchParams.get('formId');

        // console.log('🔍 检查URL参数:', { code, state, formId });

        // 设置返回URL和处理标记
        if (state && state.startsWith('detail/')) {
            const currentDomain = process.env.REACT_APP_AUTH_API_BASE_URL || window.location.origin;
            const fullUrl = `${currentDomain}/${state}`;
            localStorage.setItem('returnUrl', fullUrl);
            localStorage.setItem('isProcessingWechatAuth', '1');
            // console.log('WechatAuth: 已设置returnUrl和isProcessingWechatAuth', fullUrl);
        } else if (state && state === "blackcard") {
            // 黑卡页面的完整URL
            const currentDomain = process.env.REACT_APP_AUTH_API_BASE_URL || window.location.origin;
            const fullUrl = `${currentDomain}/black-card-activation-mobile`;
            // 设置returnUrl为完整路径
            localStorage.setItem('returnUrl', fullUrl);
            // 设置isProcessingWechatAuth标记
            localStorage.setItem('isProcessingWechatAuth', '1');
            // console.log('WechatAuth: 检测到黑卡页面state，设置完整URL:', fullUrl);
        } else if (state && state.startsWith("sign_in_sheet:")) {
            // 签到页面：从state中解析brand参数值，构建完整URL
            // state格式：sign_in_sheet:测试3（只保存brand参数的值，已解码）
            const brandValue = state.replace('sign_in_sheet:', ''); // 提取brand参数值
            const currentDomain = process.env.REACT_APP_AUTH_API_BASE_URL || window.location.origin;
            // 使用encodeURIComponent确保中文参数正确编码
            const fullUrl = brandValue 
                ? `${currentDomain}/sign-in-sheet-form?brand=${encodeURIComponent(brandValue)}`
                : `${currentDomain}/sign-in-sheet-form`;
            localStorage.setItem('returnUrl', fullUrl);
            localStorage.setItem('isProcessingWechatAuth', '1');
            // console.log('WechatAuth: 从state解析签到页面完整URL:', fullUrl);
            // // 签到页面：从state中解析完整路径（包括查询参数）
            // // state格式：sign_in_sheet:/sign-in-sheet-form?brand=xxx&merchant=yyy
            // // 提取查询参数部分（去掉 sign_in_sheet: 前缀和路径部分）
            // const stateWithoutPrefix = state.replace('sign_in_sheet:', ''); // /sign-in-sheet-form?brand=xxx&merchant=yyy
            // // 提取查询参数（?后面的部分）
            // const queryParams = stateWithoutPrefix.includes('?') ? stateWithoutPrefix.split('?')[1] : '';
            // // 构建返回路径：/sign-in-sheet-form? + 查询参数
            // const returnPath = queryParams ? `/sign-in-sheet-form?${queryParams}` : '/sign-in-sheet-form';
            // const currentDomain = process.env.REACT_APP_AUTH_API_BASE_URL || window.location.origin;
            // const fullUrl = `${currentDomain}${returnPath}`;
            // // 设置returnUrl为完整路径
            // localStorage.setItem('returnUrl', fullUrl);
            // // 设置isProcessingWechatAuth标记
            // localStorage.setItem('isProcessingWechatAuth', '1');
            // // console.log('WechatAuth: 从state解析签到页面完整URL:', fullUrl);
        }
        if (code && (state === 'wechat_auth' || state === 'blackcard' || state?.startsWith('sign_in_sheet:') || state?.startsWith('detail/'))) {
            // console.log('✅ WechatAuth: 检测到授权回调参数，开始处理授权');
            // console.log('📝 授权码:', code);
            // console.log('📝 状态:', state);
            handleWechatCallback(code, state);
            return;
        }

        // 检查是否已经在微信授权URL中，避免循环
        const currentUrl = window.location.href;
        // console.log('🔍 检查当前URL是否在微信授权页面:', currentUrl);

        if (currentUrl.includes('open.weixin.qq.com') || currentUrl.includes('connect/oauth2/authorize')) {
            // console.log('⚠️ WechatAuth: 当前已在微信授权页面，跳过跳转');
            return;
        }

        if (formId) {
            localStorage.setItem('wechatFormId', formId);
            // console.log('💾 WechatAuth: 保存formId:', formId);
        }

        // 检查是否在微信浏览器中
        const isWechat = isWechatBrowser();
        // console.log('🔍 WechatAuth: 微信浏览器检测结果:', isWechat);
        // console.log('📱 是否微信浏览器:', isWechat ? '是' : '否');

        if (!isWechat) {
            // console.log('❌ WechatAuth: 非微信浏览器，显示错误');
            setError('请在微信中打开此页面');
            setLoading(false);
            // setDebugInfo(prev => prev + '\n\n错误: 非微信浏览器环境');
            return;
        }

        // 检查是否已经有微信用户信息（已授权成功）
        const wechatUserStr = localStorage.getItem('wechatUser');
        if (wechatUserStr) {
            try {
                const wechatUser = JSON.parse(wechatUserStr);
                if (wechatUser && wechatUser.openid) {
                    // console.log('✅ WechatAuth: 检测到已授权用户，跳过微信授权流程');
                    // console.log('👤 已授权用户:', wechatUser.nickname || wechatUser.openid);
                    setLoading(false);
                    return;
                }
            } catch (error) {
                // console.log('⚠️ 解析wechatUser失败，继续授权流程');
            }
        }
        // 构建微信授权URL
        const authUrl = buildWechatAuthUrl();

        // 保存跳转相关信息
        saveWechatJumpInfo({
            fromPage: 'WechatAuth',
            authUrl: authUrl
        });
        setRedirectingFlag('wechat');

        // 立即跳转到微信授权页面
        // console.log('🚀 跳转到:', authUrl);
        // 使用 replace 而不是 href，避免在历史记录中留下当前页面
        window.location.replace(authUrl);

        // 清理超时定时器
        return () => {
            clearTimeout(timeoutId);
        };
    }, [searchParams]);

    const handleWechatCallback = async (code, state) => {
        // console.log('🔄 WechatAuth: 开始处理授权回调');
        // console.log('📝 授权码:', code);
        // console.log('📝 状态参数:', state);
        // console.log('📊 处理时间:', new Date().toISOString());

        try {
            // 使用配置好的API地址，而不是直接使用环境变量
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
            // console.log('🔗 API地址:', API_BASE_URL);
            // console.log('🔗 完整API端点:', `${API_BASE_URL}/auth/wechat-login`);

            const formId = localStorage.getItem('wechatFormId');
            const requestBody = {code, formId};
            // console.log('📦 请求体:', requestBody);
            // console.log('📦 请求体JSON:', JSON.stringify(requestBody));

            // console.log('📤 WechatAuth: 发送授权请求...');
            // console.log('📤 请求方法: POST');
            // console.log('📤 请求头: Content-Type: application/json');

            const response = await fetch(`${API_BASE_URL}/auth/wechat-login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            // console.log('📥 WechatAuth: 收到响应');
            // console.log('📥 响应状态:', response.status);
            // console.log('📥 响应状态文本:', response.statusText);
            // console.log('📥 响应头:', Object.fromEntries(response.headers.entries()));

            const data = await response.json();
            // console.log('📥 响应数据:', data);
            // console.log('📥 响应成功状态:', data.success);
            // console.log('📥 响应消息:', data.message);

            if (data.success) {
                // console.log('✅ WechatAuth: 授权成功，开始保存用户信息');
                // console.log('👤 用户信息:', data.user);
                // console.log('🔑 Token:', data.token);

                // 保存用户信息和token
                localStorage.setItem('wechatUser', JSON.stringify(data.user));
                localStorage.setItem('wechatToken', data.token);
                localStorage.setItem('isWechatLoggedIn', '1');
                localStorage.setItem('isLoggedIn', '1'); // 兼容主路由守卫

                // console.log('💾 用户信息已保存到localStorage');
                // console.log('💾 wechatUser:', JSON.stringify(data.user));
                // console.log('💾 wechatToken:', data.token);
                // console.log('💾 isWechatLoggedIn: 1');
                // console.log('💾 isLoggedIn: 1');

                // 获取保存的跳转路径
                let returnUrl = '/';

                // 优先从state中解析路径（签到页面和表单详情页）
                if (state && state.startsWith('sign_in_sheet:')) {
                    // 签到页面：从state中解析brand参数值，构建完整URL
                    // state格式：sign_in_sheet:测试3（只保存brand参数的值，已解码）
                    const brandValue = state.replace('sign_in_sheet:', ''); // 提取brand参数值
                    const currentDomain = process.env.REACT_APP_AUTH_API_BASE_URL || window.location.origin;
                    // 使用encodeURIComponent确保中文参数正确编码
                    returnUrl = brandValue 
                        ? `${currentDomain}/sign-in-sheet-form?brand=${encodeURIComponent(brandValue)}`
                        : `${currentDomain}/sign-in-sheet-form`;
                    // console.log('🔄 从state解析签到页面完整URL:', returnUrl);
                } else if (state && state.startsWith('detail/')) {
                    // 表单详情页：使用state作为返回URL
                    returnUrl = `/${state}`;
                    // console.log('🔄 使用state参数作为返回路径:', returnUrl);
                } else {
                    // 其他情况：使用保存的跳转路径
                    const redirectInfo = getAuthRedirectPath();
                    if (redirectInfo && redirectInfo.path) {
                        returnUrl = redirectInfo.path;
                        // console.log('🔄 使用保存的跳转路径:', returnUrl);
                    } else {
                        // 兼容旧版本的跳转逻辑
                        const oldReturnUrl = localStorage.getItem('returnUrl');
                        if (oldReturnUrl) {
                            returnUrl = oldReturnUrl;
                            // console.log('🔄 使用旧版本保存的跳转路径:', returnUrl);
                        }
                    }
                }

                // console.log('🔄 准备跳转回:', returnUrl);
                // console.log('🔄 当前域名:', window.location.origin);
                // console.log('🔄 目标URL域名:', new URL(returnUrl, window.location.origin).origin);
                // console.log('📊 跳转时间:', new Date().toISOString());

                // 清理跳转标记和路径（在跳转前清理）
                localStorage.removeItem('isRedirectingToAuth');
                localStorage.removeItem('isRedirectingToWechat');
                clearAuthRedirectPath();
                // console.log('🧹 已清理跳转标记和路径');

                // 立即跳转回原页面
                // console.log('🚀 跳转回:', returnUrl);
                // 使用 replace 跳转，避免在历史记录中留下当前页面
                window.location.replace(returnUrl);
            } else {
                // console.log('❌ WechatAuth: 授权失败');
                // console.log('❌ 失败消息:', data.message || data.error);
                // console.log('❌ 完整响应:', data);
                throw new Error(data.message || data.error || '微信授权失败');
            }
        } catch (error) {
            // console.error('💥 WechatAuth: 微信授权回调处理失败');
            // console.error('💥 错误详情:', error);
            // console.error('💥 错误消息:', error.message);
            // console.error('💥 错误堆栈:', error.stack);

            const errorInfo = {
                message: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent
            };

            setError('微信授权失败，请联系管理员或稍后重试。\n\n错误详情: ' + error.message);
            // setDebugInfo(prev => prev + '\n\n授权失败错误:\n' + JSON.stringify(errorInfo, null, 2));
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: '#f5f7fa',
                padding: '20px'
            }}>
                <div style={{
                    width: 40,
                    height: 40,
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #1890ff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{marginTop: 16, color: '#666', textAlign: 'center'}}>
                    正在跳转到微信授权...
                </p>
                <p style={{marginTop: 8, color: '#999', fontSize: '14px', textAlign: 'center'}}>
                    如果长时间无响应，请刷新页面重试
                </p>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        marginTop: 16,
                        padding: '8px 16px',
                        background: '#1890ff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    刷新页面
                </button>
                <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
            </div>
        );
    }

    // 错误提示界面已注释 - 不应该对用户开放详细错误信息
    // if (error) {
    //     return (
    //         <div style={{
    //             display: 'flex',
    //             flexDirection: 'column',
    //             alignItems: 'center',
    //             justifyContent: 'center',
    //             minHeight: '100vh',
    //             background: '#f5f7fa',
    //             padding: '20px'
    //         }}>
    //             <div style={{color: 'red', fontSize: '18px', marginBottom: '16px', textAlign: 'center'}}>
    //                 {error}
    //             </div>

    //             {/* 调试信息显示 */}
    //             {debugInfo && (
    //                 <div style={{
    //                     width: '100%',
    //                     maxWidth: '800px',
    //                     marginBottom: '16px',
    //                     padding: '16px',
    //                     background: '#f0f0f0',
    //                     borderRadius: '8px',
    //                     border: '1px solid #d9d9d9'
    //                 }}>
    //                     <h4 style={{margin: '0 0 12px 0', color: '#333'}}>调试信息:</h4>
    //                     <pre style={{
    //                         fontSize: '12px',
    //                         color: '#666',
    //                         whiteSpace: 'pre-wrap',
    //                         wordBreak: 'break-all',
    //                         margin: 0,
    //                         maxHeight: '300px',
    //                         overflow: 'auto'
    //                     }}>
    //                         {debugInfo}
    //                     </pre>
    //                 </div>
    //             )}

    //             <div style={{marginBottom: '16px', textAlign: 'center'}}>
    //                 <p style={{color: '#666', marginBottom: '8px'}}>可能的解决方案：</p>
    //                 <p style={{color: '#999', fontSize: '14px'}}>1. 确保在微信中打开此页面</p>
    //                 <p style={{color: '#999', fontSize: '14px'}}>2. 检查网络连接是否正常</p>
    //                 <p style={{color: '#999', fontSize: '14px'}}>3. 尝试刷新页面重试</p>
    //             </div>
    //             <div style={{display: 'flex', gap: '12px'}}>
    //                 <button
    //                     onClick={() => window.history.back()}
    //                     style={{
    //                         padding: '8px 16px',
    //                         background: '#1890ff',
    //                         color: 'white',
    //                         border: 'none',
    //                         borderRadius: '4px',
    //                         cursor: 'pointer'
    //                     }}
    //                 >
    //                     返回
    //                 </button>
    //                 <button
    //                     onClick={() => window.location.reload()}
    //                     style={{
    //                         padding: '8px 16px',
    //                         background: '#52c41a',
    //                         color: 'white',
    //                         border: 'none',
    //                         borderRadius: '4px',
    //                         cursor: 'pointer'
    //                     }}
    //                 >
    //                     刷新页面
    //                 </button>
    //                 <button
    //                     onClick={() => {
    //                         navigator.clipboard.writeText(debugInfo).then(() => {
    //                             alert('调试信息已复制到剪贴板');
    //                         });
    //                     }}
    //                     style={{
    //                         padding: '8px 16px',
    //                         background: '#722ed1',
    //                         color: 'white',
    //                         border: 'none',
    //                         borderRadius: '4px',
    //                         cursor: 'pointer'
    //                     }}
    //                 >
    //                     复制调试信息
    //                 </button>
    //             </div>
    //         </div>
    //     );
    // }

    // 默认fallback，避免完全白屏
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: '#f5f7fa',
            padding: '20px'
        }}>
            <div style={{
                width: 40,
                height: 40,
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #1890ff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{marginTop: 16, color: '#666', textAlign: 'center'}}>
                正在处理微信授权...
            </p>

            {/* 显示调试信息 - 已注释 */}
            {/* {debugInfo && (
                <div style={{
                    width: '100%',
                    maxWidth: '800px',
                    marginTop: '20px',
                    padding: '16px',
                    background: '#f0f0f0',
                    borderRadius: '8px',
                    border: '1px solid #d9d9d9'
                }}>
                    <h4 style={{margin: '0 0 12px 0', color: '#333'}}>当前状态调试信息:</h4>
                    <pre style={{
                        fontSize: '12px',
                        color: '#666',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        margin: 0,
                        maxHeight: '200px',
                        overflow: 'auto'
                    }}>
                        {debugInfo}
                    </pre>
                </div>
            )} */}

            <div style={{display: 'flex', gap: '12px', marginTop: '16px'}}>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        padding: '8px 16px',
                        background: '#1890ff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    刷新页面
                </button>
                {/* 复制调试信息按钮 - 已注释 */}
                {/* {debugInfo && (
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(debugInfo).then(() => {
                                alert('调试信息已复制到剪贴板');
                            });
                        }}
                        style={{
                            padding: '8px 16px',
                            background: '#722ed1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        复制调试信息
                    </button>
                )} */}
            </div>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
} 